import { useSearchParams } from 'react-router-dom'
import type { Billing, PlanInfo, Workspace } from '../api/types'
import { LedgerTable } from '../components/LedgerTable'
import { Badge, Button, Card, EmptyState, ErrorNote, LoadingBlock, PageHeader, Stat } from '../components/ui'
import { useBilling, useCheckout, usePortal } from '../hooks/useBilling'
import { useCurrentWorkspace } from '../hooks/useWorkspace'
import { formatBytes, formatCredits } from '../utils/format'

const dollars = (cents: number) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`

export function BillingPage() {
  const { workspace } = useCurrentWorkspace()
  if (!workspace) return <EmptyState title="No workspace" body="Create a workspace to see its plan and credits." />
  return <BillingView workspace={workspace} />
}

function BillingView({ workspace }: { workspace: Workspace }) {
  const billing = useBilling(workspace.id)
  const [params] = useSearchParams()
  const outcome = params.get('checkout')
  if (billing.isPending) return <LoadingBlock />
  if (billing.isError) return <ErrorNote error={billing.error} />
  const data = billing.data
  const { balance, plan } = data
  const premium = plan.plan === 'premium'
  return (
    <>
      <PageHeader title="Billing" subtitle={`${workspace.name} · ${premium ? 'Premium' : 'Free'} plan`} />
      <div className="stack-lg">
        {data.suspended && (
          <ErrorNote
            message={`This workspace has been stopped, so nothing new can be made or sent. Everything already here is safe, and you can still pay.${
              data.suspended_reason ? ` Reason given: ${data.suspended_reason}` : ''
            }`}
          />
        )}
        {outcome === 'success' && <div className="notice">Thanks, your payment went through. New credits or a new plan can take a few seconds to show here.</div>}
        {outcome === 'cancelled' && <div className="notice">Checkout was cancelled. Nothing was charged.</div>}
        <div className="grid-4">
          <Stat label="Available" value={formatCredits(balance.available)} note="Credits you can spend now" />
          <Stat
            label={premium ? 'This billing period' : 'This month'}
            value={formatCredits(balance.allowance)}
            note={`of ${plan.monthly_credits.toLocaleString('en-US')} included`}
          />
          <Stat label="Extra" value={formatCredits(balance.extra)} note="Top-ups and grants. They never expire." />
          <Stat label="Held" value={formatCredits(balance.held, 'up')} note="Set aside for work in progress" />
          <Stat
            label="Storage"
            value={formatBytes(data.stored_bytes)}
            note={`of ${formatBytes(plan.max_storage_bytes)} · recordings and everything made from them`}
          />
        </div>
        <div className="grid-2">
          <PlanCard workspace={workspace} billing={data} />
          <PacksCard workspace={workspace} billing={data} />
        </div>
        <Card title="What things cost">
          <div className="stack">
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {data.prices.map((price) => (
                    <tr key={`${price.task}/${price.unit}`}>
                      <td>{price.label}</td>
                      <td className="num">{formatCredits(price.micro_credits, 'exact')} credits</td>
                    </tr>
                  ))}
                  <tr className="is-muted">
                    <td>Processing the recording into playback files</td>
                    <td className="num">Free</td>
                  </tr>
                  <tr className="is-muted">
                    <td>Listeners downloading a published episode</td>
                    <td className="num">Free</td>
                  </tr>
                  <tr className="is-muted">
                    <td>Cleaning up an episode's sound</td>
                    <td className="num">Free</td>
                  </tr>
                </tbody>
              </table>
            </div>
            {data.written_off > 0 && (
              <p className="muted small">
                {formatCredits(data.written_off, 'up')} credits of work cost more than this workspace had. It ran anyway and you were not billed for
                it, but work stops when the balance runs out.
              </p>
            )}
            <p className="muted small">
              Nothing here runs on its own: every charge follows something you asked for. An hour-long episode, if you ask for its transcript,
              episode pack, clip ideas and a thumbnail, comes to about 900 credits. Work holds its estimate when it starts, is charged what it
              actually used when it finishes, and costs nothing if it fails.
            </p>
          </div>
        </Card>
        <Card title="History">
          {data.ledger.length === 0 ? <p className="muted">Nothing yet.</p> : <LedgerTable entries={data.ledger} />}
        </Card>
      </div>
    </>
  )
}

function PlanCard({ workspace, billing }: { workspace: Workspace; billing: Billing }) {
  const checkout = useCheckout(workspace.id)
  const portal = usePortal(workspace.id)
  const owner = workspace.role === 'owner'
  const premium = billing.plan.plan === 'premium'
  const subscription = billing.subscription
  const rows: [string, (p: PlanInfo) => string][] = [
    ['Credits each month', (p) => p.monthly_credits.toLocaleString('en-US')],
    ['Shows', (p) => (p.max_shows == null ? 'Unlimited' : String(p.max_shows))],
    ['People in the workspace', (p) => (p.max_members > 1 ? `Up to ${p.max_members}` : 'Just you')],
    ['Clips', (p) => (p.watermark ? 'Small indiefuture mark' : 'No mark')],
    ['Original recordings kept', (p) => `${p.retention_days} days`],
    ['Live from OBS', (p) => (p.live ? 'Included' : '—')],
    ['Longest broadcast', (p) => (p.max_broadcast_hours > 0 ? `${p.max_broadcast_hours} hours` : '—')],
    ['Storage', (p) => formatBytes(p.max_storage_bytes)],
  ]
  return (
    <Card title="Plan" actions={<Badge tone={premium ? 'accent' : 'neutral'}>{premium ? 'Premium' : 'Free'}</Badge>}>
      <div className="stack">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th />
                {billing.plans.map((p) => (
                  <th key={p.plan} className={p.plan === billing.plan.plan ? 'current' : undefined}>
                    {p.plan === 'premium' ? `Premium · ${dollars(billing.premium_price_cents)}/mo` : 'Free'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(([label, value]) => (
                <tr key={label}>
                  <td className="muted">{label}</td>
                  {billing.plans.map((p) => (
                    <td key={p.plan}>{value(p)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {subscription && subscription.current_period_end && (
          <p className="muted small">
            {subscription.cancel_at_period_end ? 'Ends' : 'Renews'} on {new Date(subscription.current_period_end).toLocaleDateString()} ({subscription.status}).
          </p>
        )}
        <div className="row">
          {!premium && (
            <Button busy={checkout.isPending} disabled={!owner || !billing.premium_ready} onClick={() => checkout.mutate({ kind: 'premium' })}>
              Upgrade to Premium
            </Button>
          )}
          {billing.has_customer && (
            <Button variant="secondary" busy={portal.isPending} disabled={!owner} onClick={() => portal.mutate()}>
              Manage billing
            </Button>
          )}
        </div>
        {!owner && <p className="muted small">Only the workspace owner can change billing.</p>}
        {owner && !billing.stripe_ready && <p className="muted small">Payments are not switched on yet.</p>}
        {(checkout.error ?? portal.error) && <ErrorNote error={checkout.error ?? portal.error} />}
      </div>
    </Card>
  )
}

function PacksCard({ workspace, billing }: { workspace: Workspace; billing: Billing }) {
  const checkout = useCheckout(workspace.id)
  const owner = workspace.role === 'owner'
  const buying = checkout.isPending ? checkout.variables : undefined
  return (
    <Card title="Top up">
      <div className="stack">
        <div className="pack-list">
          {billing.packs.map((pack) => (
            <div key={pack.credits} className="pack">
              <div>
                <strong>{pack.credits.toLocaleString('en-US')} credits</strong>
                <div className="muted small">{dollars(pack.amount_cents)}, one time</div>
              </div>
              <Button
                variant="secondary"
                busy={buying?.kind === 'pack' && buying.credits === pack.credits}
                disabled={!owner || !billing.stripe_ready || checkout.isPending}
                onClick={() => checkout.mutate({ kind: 'pack', credits: pack.credits })}
              >
                Buy
              </Button>
            </div>
          ))}
        </div>
        <p className="muted small">Extra credits never expire. The monthly credits are used first.</p>
        {checkout.error && <ErrorNote error={checkout.error} />}
      </div>
    </Card>
  )
}
